import React, { useState } from "react";
import "../../../../styles/AuditTrail.css";
import { Spinner } from "react-bootstrap";
import { MdOutlineNavigateNext, MdOutlineNavigateBefore } from "react-icons/md";

import { useGetAuditLogsQuery } from "api/ruleEngineApi";
import { showToastError } from "components/toastify/ToastNotifications";

const PAGE_SIZE = 10; // default/fallback – adjust if your API uses a different default

const AuditTrailPage: React.FC = () => {
  const [currentPage, setCurrentPage] = React.useState(1);

  const {
    data: auditLogsData,
    error: auditLogsError,
    isFetching: isFetchingAuditLogs,
  } = useGetAuditLogsQuery({ page: currentPage });

  React.useEffect(() => {
    if (auditLogsError) {
      console.error("Audit Logs Error:", auditLogsError);
      showToastError("Failed to load audit logs");
    }
  }, [auditLogsError]);
  React.useEffect(() => {
    if (auditLogsData) {
      
    }
  }, [auditLogsData]);

  const parseBusinessLine = (newValueStr: string | null): string => {
    if (!newValueStr) return "N/A";
    try {
      const parsed = JSON.parse(newValueStr);
      return parsed.BusinessLine || "N/A";
    } catch {
      return "N/A";
    }
  };

  const formatTimestamp = (
    _timestamp: string,
    date: string,
    time: string
  ): string => {
    return `${date} ${time}`;
  };

  // Derive paging values with safe fallbacks
  const pageNumber = auditLogsData?.data?.pageNumber ?? currentPage;
  const pageSize = auditLogsData?.data?.pageSize ?? PAGE_SIZE;
  const totalPages = auditLogsData?.data?.totalPages ?? 1;
  const totalSize = auditLogsData?.data?.totalSize ?? 0;
  const items = auditLogsData?.data?.items ?? [];

  // Utility component for truncating & expanding values
  const TruncatedValue: React.FC<{ value: string | null }> = ({ value }) => {
    const [expanded, setExpanded] = useState(false);

    if (!value) return <span>-</span>;

    const displayValue = expanded
      ? value
      : value.length > 25
      ? value.substring(0, 25) + "..."
      : value;

    return (
      <span>
        {displayValue}
        {value.length > 25 && (
          <button
            onClick={() => setExpanded((prev: any) => !prev)}
            className="view-more-btn"
            style={{
              marginLeft: "6px",
              fontSize: "0.8rem",
              color: "#007bff",
              background: "none",
              border: "none",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {expanded ? "View less" : "View more"}
          </button>
        )}
      </span>
    );
  };

  // S/N should continue across pages
  const getSerialNumber = (index: number): number => {
    return (currentPage - 1) * pageSize + index + 1;
  };

  const handlePageChange = (page: number) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const renderPaginationButtons = () => {
    if (!auditLogsData?.data || totalPages <= 1) return null;

    const buttons: React.ReactNode[] = [];

    if (pageNumber > 1) {
      buttons.push(
        <button
          key="prev"
          className="page-button nav-button"
          onClick={() => handlePageChange(pageNumber - 1)}
          disabled={isFetchingAuditLogs}
        >
          <MdOutlineNavigateBefore />
        </button>
      );
    }

    const startPage = Math.max(1, pageNumber - 2);
    const endPage = Math.min(totalPages, pageNumber + 2);

    if (startPage > 1) {
      buttons.push(
        <button
          key={1}
          className="page-button"
          onClick={() => handlePageChange(1)}
          disabled={isFetchingAuditLogs}
        >
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(
          <span key="ellipsis1" className="pagination-ellipsis">
            ...
          </span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          className={`page-button ${pageNumber === i ? "active" : ""}`}
          onClick={() => handlePageChange(i)}
          disabled={isFetchingAuditLogs}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <span key="ellipsis2" className="pagination-ellipsis">
            ...
          </span>
        );
      }
      buttons.push(
        <button
          key={totalPages}
          className="page-button"
          onClick={() => handlePageChange(totalPages)}
          disabled={isFetchingAuditLogs}
        >
          {totalPages}
        </button>
      );
    }

    if (pageNumber < totalPages) {
      buttons.push(
        <button
          key="next"
          className="page-button nav-button"
          onClick={() => handlePageChange(pageNumber + 1)}
          disabled={isFetchingAuditLogs}
        >
          <MdOutlineNavigateNext />
        </button>
      );
    }

    return buttons;
  };

  return (
    <div className="audit-trail-page">
      <div className="audit-trail-section">
        <div className="audit-table-container">
          {isFetchingAuditLogs ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "200px" }}
            >
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <table className="audit-table">
              <thead>
                <tr>
                  <th>S/N</th>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Revenue Line</th>
                  <th>Field</th>
                  <th>Old Value</th>
                  <th>New Value</th>
                  {/* <th>Version</th> */}
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? (
                  items.map((entry: any, index: number) => (
                    <tr key={entry.id || index}>
                      <td className="sn-cell">{getSerialNumber(index)}</td>
                      <td className="timestamp-cell">
                        {formatTimestamp(
                          entry.timestamp,
                          entry.date,
                          entry.time
                        )}
                      </td>
                      <td className="user-cell">{entry.performedBy}</td>
                      <td className="action-cell">{entry.actionType}</td>
                      <td className="revenue-line-cell">
                        {parseBusinessLine(entry.newValue)}
                      </td>
                      <td className="field-cell">{entry.fieldChanged}</td>
                      <td className="old-value-cell">
                        <TruncatedValue value={entry.oldValue} />
                      </td>
                      <td className="new-value-cell">
                        <TruncatedValue value={entry.newValue} />
                      </td>
                      {/* <td className="version-cell">
                        <span className="version-badge">
                          v{getSerialNumber(index)}
                        </span>
                      </td> */}
                      <td className="reason-cell">{entry.reason}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="text-center">
                      {auditLogsError
                        ? "Error loading data"
                        : "No audit logs available"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && !isFetchingAuditLogs && (
          <div className="audit-pagination">
            <span className="pagination-info">
              Showing {(pageNumber - 1) * pageSize + (items.length ? 1 : 0)} -{" "}
              {(pageNumber - 1) * pageSize + items.length} of {totalSize}
            </span>
            <div className="pagination-controls">
              {renderPaginationButtons()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditTrailPage;
